import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ArrowRight, Award, RefreshCw, AlertCircle, BookOpen } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { useSfx, SoundKey } from '../../../store/sfxStore';
import { COURSE_QUIZZES } from '../../../data/quizzes';
import { CourseId } from '../../../types';
import { ErrorBoundary } from 'react-error-boundary';
import { QuizErrorFallback } from './QuizErrorFallback';

// --- Types ---
interface QuizProps {
    courseId: CourseId;
    courseTitle: string;
    onClose: () => void;
}

interface QuestionState {
    text: string;
    options: string[];
    correctIndex: number;
    explanation: string;
}

// --- Logic Helper: Safe Shuffle ---
const safelyPrepareQuestions = (courseId: string): QuestionState[] => {
    try {
        // 1. Safe Access
        const rawData = COURSE_QUIZZES?.[courseId as CourseId];

        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            console.warn(`[QuizEngine] No questions found for course: ${courseId}`);
            return [];
        }

        // 2. Clone & Shuffle
        const shuffledQuestions = [...rawData].sort(() => Math.random() - 0.5);

        // 3. Process Options
        return shuffledQuestions.map(q => {
            if (!q.options || !q.text) return null; // Skip invalid questions

            const optionsWithIndices = q.options.map((opt, idx) => ({
                text: opt,
                isCorrect: idx === q.correctIndex
            }));

            // Shuffle options
            const shuffledOps = optionsWithIndices.sort(() => Math.random() - 0.5);

            return {
                text: q.text,
                options: shuffledOps.map(o => o.text),
                correctIndex: shuffledOps.findIndex(o => o.isCorrect),
                explanation: q.explanation || "No explanation provided."
            };
        }).filter(Boolean) as QuestionState[]; // Remove nulls
    } catch (err) {
        console.error("[QuizEngine] Critical Error preparing questions:", err);
        return [];
    }
};

// --- Core Component ---
const QuizLogic = ({ courseId, courseTitle, onClose }: QuizProps) => {
    // State
    const [questions, setQuestions] = useState<QuestionState[]>([]);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'empty'>('loading');

    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [hasFailed, setHasFailed] = useState(false);

    // Interaction State
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    // Stores
    const { completeCourse, completeModule } = useAcademyStore();
    const { play } = useSfx();

    // 1. Initialize
    useEffect(() => {
        const prepared = safelyPrepareQuestions(courseId);
        if (prepared.length > 0) {
            setQuestions(prepared);
            setStatus('ready');
        } else {
            setStatus('empty');
        }
    }, [courseId]);

    // 2. Safe Play Wrapper
    const safePlay = useCallback((key: SoundKey) => {
        if (play) {
            try { play(key); }
            catch (e) { console.warn("SFX Fail", e); }
        }
    }, [play]);

    // 3. Handlers
    const handleSelect = (idx: number) => {
        if (isAnswered) return;
        setSelectedOption(idx);
        safePlay('click');
    };

    const handleSubmit = () => {
        if (selectedOption === null) return;
        setIsAnswered(true);

        const currentQ = questions[currentIndex];
        const isCorrect = selectedOption === currentQ.correctIndex;

        if (isCorrect) {
            setScore(s => s + 1);
            safePlay('success');
        } else {
            setHasFailed(true);
            safePlay('error');
        }
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(i => i + 1);
            setSelectedOption(null);
            setIsAnswered(false);
            safePlay('click');
        } else {
            finishQuiz();
        }
    };

    const finishQuiz = () => {
        setIsComplete(true);
        const passed = !hasFailed && (score + (selectedOption === questions[currentIndex]?.correctIndex ? 0 : 0)) === questions.length;
        // Note: Score logic above is handled in real-time, checking final state

        // Final Score Calculation Check
        const finalScore = score; // Already updated in handleSubmit

        if (!hasFailed && finalScore === questions.length) {
            console.log("🏆 Quiz Passed:", courseId);
            safePlay('glory');
            completeModule(`${courseId}-final-exam`, 1000);
            completeCourse(courseId);
        } else {
            console.log("❌ Quiz Failed:", courseId);
            safePlay('error');
        }
    };

    // --- Renders ---

    if (status === 'loading') {
        return (
            <div className="bg-white p-12 rounded-3xl shadow-xl text-center animate-pulse">
                <BookOpen className="mx-auto mb-4 text-slate-300" size={48} />
                <h3 className="text-xl font-bold text-slate-400">Loading Exam...</h3>
            </div>
        );
    }

    if (status === 'empty') {
        return (
            <div className="bg-white p-12 rounded-3xl shadow-xl text-center border-4 border-slate-100">
                <AlertCircle className="mx-auto mb-4 text-slate-300" size={48} />
                <h3 className="text-xl font-bold text-slate-500 mb-2">No Quiz Available</h3>
                <p className="text-slate-400 mb-6">This course doesn't have a final exam yet.</p>
                <button onClick={onClose} className="px-6 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold text-slate-600 transition-colors">
                    Close
                </button>
            </div>
        );
    }

    // Results View
    if (isComplete) {
        const passed = !hasFailed; // Strict 100% rule
        return (
            <div className="bg-white p-8 md:p-12 rounded-[2rem] shadow-2xl text-center max-w-2xl mx-auto border-4 border-slate-50">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${passed ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}
                >
                    {passed ? <Award size={48} strokeWidth={3} /> : <X size={48} strokeWidth={3} />}
                </motion.div>

                <h2 className="text-3xl md:text-4xl font-black text-slate-800 mb-4 font-comic">
                    {passed ? 'Outstanding!' : 'Review Needed'}
                </h2>

                <p className="text-lg text-slate-500 font-medium mb-10">
                    {passed
                        ? `You have officially mastered the ${courseTitle}. Your certificate is ready.`
                        : `You scored ${score} out of ${questions.length}. You need 100% to pass this exam.`
                    }
                </p>

                <div className="flex gap-4 justify-center">
                    <button
                        onClick={onClose}
                        className="px-8 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl transition-all"
                    >
                        {passed ? 'Collect Badge' : 'Close'}
                    </button>
                    {!passed && (
                        <button
                            onClick={() => {
                                // Reset Local State
                                setStatus('loading');
                                setTimeout(() => {
                                    setQuestions(safelyPrepareQuestions(courseId));
                                    setCurrentIndex(0);
                                    setScore(0);
                                    setHasFailed(false);
                                    setIsComplete(false);
                                    setIsAnswered(false);
                                    setSelectedOption(null);
                                    setStatus('ready');
                                }, 300);
                            }}
                            className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl shadow-lg hover:shadow-blue-200 transition-all flex items-center gap-2"
                        >
                            <RefreshCw size={20} /> Retry
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Active Question View
    const currentQ = questions[currentIndex];
    const progressPercent = ((currentIndex) / questions.length) * 100;

    return (
        <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden max-w-3xl mx-auto border-4 border-slate-50 relative">
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-slate-100">
                <motion.div
                    className="h-full bg-blue-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                />
            </div>

            {/* Header */}
            <div className="p-8 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <div>
                    <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Final Exam</h2>
                    <h3 className="text-xl font-black text-slate-800">{courseTitle}</h3>
                </div>
                <div className="text-right">
                    <span className="text-3xl font-black text-slate-200">{currentIndex + 1}</span>
                    <span className="text-lg font-bold text-slate-200">/{questions.length}</span>
                </div>
            </div>

            {/* Content */}
            <div className="p-8 md:p-10">
                <h4 className="text-2xl font-bold text-slate-800 mb-8 leading-snug">
                    {currentQ.text}
                </h4>

                <div className="space-y-4">
                    {currentQ.options.map((opt, idx) => {
                        let stateStyles = "border-slate-200 hover:border-blue-300 hover:bg-slate-50";
                        if (isAnswered) {
                            if (idx === currentQ.correctIndex) stateStyles = "border-green-500 bg-green-50 text-green-700 ring-1 ring-green-200";
                            else if (idx === selectedOption) stateStyles = "border-red-400 bg-red-50 text-red-700";
                            else stateStyles = "border-slate-100 opacity-50 grayscale";
                        } else if (selectedOption === idx) {
                            stateStyles = "border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-100 scale-[1.02]";
                        }

                        return (
                            <button
                                key={idx}
                                onClick={() => handleSelect(idx)}
                                disabled={isAnswered}
                                className={`w-full p-5 rounded-2xl border-2 text-left font-bold text-lg transition-all duration-200 flex justify-between items-center ${stateStyles}`}
                            >
                                <span>{opt}</span>
                                {isAnswered && idx === currentQ.correctIndex && <Check className="text-green-500" />}
                                {isAnswered && idx === selectedOption && idx !== currentQ.correctIndex && <X className="text-red-500" />}
                            </button>
                        );
                    })}
                </div>

                {/* Explanation */}
                <AnimatePresence>
                    {isAnswered && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-6 p-6 bg-blue-50 rounded-2xl border border-blue-100 text-blue-800"
                        >
                            <span className="text-xs font-bold uppercase tracking-wider opacity-50 block mb-1">Knowledge Drop</span>
                            {currentQ.explanation}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Footer Actions */}
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                {!isAnswered ? (
                    <button
                        onClick={handleSubmit}
                        disabled={selectedOption === null}
                        className={`px-8 py-3 rounded-xl font-bold text-lg transition-all ${selectedOption !== null
                            ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-200/50 hover:translate-y-[-2px]'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                    >
                        Check Answer
                    </button>
                ) : (
                    <button
                        onClick={handleNext}
                        className="px-8 py-3 rounded-xl font-bold text-lg bg-green-500 text-white hover:bg-green-400 shadow-lg shadow-green-200/50 hover:translate-y-[-2px] transition-all flex items-center gap-2"
                    >
                        {currentIndex === questions.length - 1 ? 'Finish Exam' : 'Next Question'} <ArrowRight size={20} />
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Exported Safe Runner ---
export const QuizRunner: React.FC<QuizProps> = (props) => {
    return (
        <ErrorBoundary
            FallbackComponent={QuizErrorFallback}
            onReset={() => {
                // Optional: Clear any related local storage or state if needed
                console.log("Resetting Quiz Boundary");
            }}
        >
            <QuizLogic {...props} />
        </ErrorBoundary>
    );
};
